import { FunctionComponent, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { format } from "date-fns";
import { FEATURE_PROJECT_GOALS } from "../../api/query";
import SkeletonLoader from "../common/SkeletonLoader";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GoalTask {
    id: string;
    title: string;
    assigned_to_id: number;
    assigned_to_name: string;
}

interface GoalMember {
    id: string;
    member_name: string;
    employee_id: number;
}

interface Goal {
    title: string;
    goal_score: number;
    is_archive: boolean;
    start_date: string;
    end_date: string;
    description: string | null;
    created_by_name: string;
    number_of_tasks: number;
    number_of_completed_tasks: number;
    goal_status: string;
    goal_static_outcome: string | null;
    tasks: GoalTask[];
    goal_members: GoalMember[];
}

interface GroupedGoal {
    status_name: string;
    status_id: number;
    goals: Goal[];
}

/** A flat task result enriched with its parent goal context */
interface TaskResult {
    task: GoalTask;
    goal: Goal;
}

type SearchMode = "feature" | "task";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalise(s: string) {
    return s.toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();
}

function fuzzyMatch(query: string, target: string): boolean {
    const q = normalise(query);
    const t = normalise(target);
    if (!q) return true;
    return q.split(/\s+/).every((word) => t.includes(word));
}

function formatDate(dateStr: string) {
    try {
        return format(new Date(dateStr), "MM-dd-yyyy");
    } catch {
        return dateStr;
    }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
    const colorMap: Record<string, string> = {
        Closed: "bg-green-100 text-green-700 border-green-400",
        "In Progress": "bg-blue-100 text-blue-700 border-blue-400",
        "Not Started": "bg-gray-100 text-gray-600 border-gray-400",
        Blocked: "bg-red-100 text-red-700 border-red-400",
    };
    const cls = colorMap[status] ?? "bg-yellow-100 text-yellow-700 border-yellow-400";
    return (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
            {status}
        </span>
    );
}

/** One iteration card — used in Feature mode */
function IterationCard({ goal, index }: { goal: Goal; index: number }) {
    const [expanded, setExpanded] = useState(false);

    const allMembers = useMemo(() => {
        const map = new Map<number, string>();
        goal.goal_members.forEach((m) => map.set(m.employee_id, m.member_name));
        goal.tasks.forEach((t) => {
            if (t.assigned_to_id && !map.has(t.assigned_to_id))
                map.set(t.assigned_to_id, t.assigned_to_name);
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [goal]);

    return (
        <div className="border border-gray-200 dark:border-slate-700 rounded-lg dark:bg-slate-800 overflow-hidden">
            <div
                className="flex  items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors employye-item-list"
                onClick={() => setExpanded((v) => !v)}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-main-blue text-white text-xs font-bold flex items-center justify-center">
                        {index + 1}
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-white m-0 truncate">
                            {formatDate(goal.start_date)} → {formatDate(goal.end_date)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 m-0">
                            By {goal.created_by_name} · {goal.number_of_completed_tasks}/{goal.number_of_tasks} tasks done
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <StatusBadge status={goal.goal_status} />
                    <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-gray-100 dark:border-slate-700 px-0 py-3 grid gap-3">
                    {goal.description && (
                        <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide m-0 mb-1">Description</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 m-0 whitespace-pre-line">{goal.description}</p>
                        </div>
                    )}
                    {goal.goal_static_outcome && (
                        <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide m-0 mb-1">Outcome</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 m-0 whitespace-pre-line">{goal.goal_static_outcome}</p>
                        </div>
                    )}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide m-0 mb-1">
                            Team Members ({allMembers.length})
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {allMembers.map((m) => (
                                <span key={m.id} className="text-xs bg-blue-50 dark:bg-slate-700 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-600 rounded-full px-2 py-0.5">
                                    {m.name}
                                </span>
                            ))}
                        </div>
                    </div>
                    {goal.tasks.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide m-0 mb-1">
                                Tasks ({goal.tasks.length})
                            </p>
                            <ul className="m-0 p-0 list-none grid gap-1 taskhistory-blog">
                                {goal.tasks.map((t) => (
                                    <li key={t.id} className="flex items-start justify-between gap-2 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-slate-800 rounded px-2 py-1">
                                        <span className="flex-1">{t.title}</span>
                                        <span className="text-xs bg-blue-50 dark:bg-slate-700 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-600 rounded-full px-2 py-0.5">{t.assigned_to_name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/** Feature summary — used in Feature mode after selecting a suggestion */
function FeatureSummary({ featureName, iterations }: { featureName: string; iterations: Goal[] }) {
    const allContributors = useMemo(() => {
        const map = new Map<number, string>();
        iterations.forEach((g) => {
            g.goal_members.forEach((m) => map.set(m.employee_id, m.member_name));
            g.tasks.forEach((t) => {
                if (t.assigned_to_id && !map.has(t.assigned_to_id))
                    map.set(t.assigned_to_id, t.assigned_to_name);
            });
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [iterations]);

    const totalTasks = iterations.reduce((s, g) => s + g.number_of_tasks, 0);
    const completedTasks = iterations.reduce((s, g) => s + g.number_of_completed_tasks, 0);

    return (
        <div className="flex flex-col gap-4">
            <div className=" dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 py-4 grid gap-[16px]">
                <h6 className="m-0 mb-3 text-base font-bold text-gray-800 dark:text-white">{featureName}</h6>
                <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-blue-50 dark:bg-slate-700 rounded-lg p-2">
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 m-0">{iterations.length}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 m-0">{iterations.length === 1 ? "Iteration" : "Iterations"}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-slate-700 rounded-lg p-2">
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400 m-0">{allContributors.length}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 m-0">Contributors</p>
                    </div>
                    <div className="bg-purple-50 dark:bg-slate-700 rounded-lg p-2">
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 m-0">{completedTasks}/{totalTasks}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 m-0">Tasks Done</p>
                    </div>
                </div>
                <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide m-0 mb-1">All Contributors</p>
                    <div className="flex flex-wrap gap-1">
                        {allContributors.map((c) => (
                            <span key={c.id} className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-600 rounded-full px-2 py-0.5">
                                {c.name}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
            <div>
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 m-0 mb-2">
                    Iteration History (oldest → newest)
                </p>
                <div className="flex flex-col gap-2">
                    {iterations.map((g, i) => (
                        <IterationCard key={`${g.start_date}-${i}`} goal={g} index={i} />
                    ))}
                </div>
            </div>
        </div>
    );
}

/** Task result card — used in Task mode */
function TaskResultCard({ task, goal }: { task: GoalTask; goal: Goal }) {
    return (
        <div className="self-stretch flex-1 services-item-box flex flex-col items-start  gap-[8px] text-dimgray-200 p-4">
            {/* Task title */}
            <p className="text-sm font-semibold text-gray-800 dark:text-white m-0">{task.title}</p>
            <hr className="border-gray-100 dark:border-slate-700 m-0" />
            <div className="grid gap-1.5">
                {/* Parent feature */}
                <div className="flex items-start gap-2 text-xs">
                    <span className="w-24 flex-shrink-0 text-gray-600 dark:text-gray-300 font-medium">Feature</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{goal.title}</span>
                </div>
                {/* Assignee */}
                <div className="flex items-start gap-2 text-xs">
                    <span className="w-24 flex-shrink-0 text-gray-600 dark:text-gray-300 font-medium">Assigned To</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{task.assigned_to_name || "—"}</span>
                </div>
                {/* Date range */}
                <div className="flex items-start gap-2 text-xs">
                    <span className="w-24 flex-shrink-0 text-gray-600 dark:text-gray-300 font-medium">Sprint</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                        {formatDate(goal.start_date)} → {formatDate(goal.end_date)}
                    </span>
                </div>
                {/* Created by */}
                <div className="flex items-start gap-2 text-xs">
                    <span className="w-24 flex-shrink-0 text-gray-600 dark:text-gray-300 font-medium">Created By</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{goal.created_by_name}</span>
                </div>

                <div className="flex items-center gap-2 text-xs">
                    
                    <div className="flex items-center justify-between mt-1">
                        <StatusBadge status={goal.goal_status} />
                        {goal.goal_static_outcome && (
                            <span className="text-xs text-[#2b2b2b] dark:text-whitre truncate ml-2">{goal.goal_static_outcome}</span>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export type FeatureTabProps = {
    heading?: string;
};

const FeatureTab: FunctionComponent<FeatureTabProps> = ({ heading = "Feature History" }) => {
    const [searchMode, setSearchMode] = useState<SearchMode>("feature");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
    const [isArchived, setIsArchived] = useState(true);

    const { loading, data, error } = useQuery(FEATURE_PROJECT_GOALS, {
        fetchPolicy: "cache-first",
        variables: { is_archived: isArchived,project_id:1 },
    });

    // Flatten all goals
    const allGoals: Goal[] = useMemo(() => {
        const groups: GroupedGoal[] = data?.project_goals?.grouped_goals ?? [];
        return groups.flatMap((g) => g.goals);
    }, [data]);

    // ── Feature mode data ──────────────────────────────────────────────────────

    const featureMap = useMemo(() => {
        const map = new Map<string, Goal[]>();
        allGoals.forEach((goal) => {
            const key = goal.title.trim();
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(goal);
        });
        map.forEach((goals) =>
            goals.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
        );
        return map;
    }, [allGoals]);

    const featureNames = useMemo(() => Array.from(featureMap.keys()), [featureMap]);

    const featureSuggestions = useMemo(() => {
        if (!searchQuery.trim()) return [];
        return featureNames.filter((name) => fuzzyMatch(searchQuery, name)).slice(0, 8);
    }, [searchQuery, featureNames]);

    // ── Task mode data ─────────────────────────────────────────────────────────

    // Flat list of all tasks with their parent goal
    const allTaskResults: TaskResult[] = useMemo(() => {
        const results: TaskResult[] = [];
        allGoals.forEach((goal) => {
            goal.tasks.forEach((task) => results.push({ task, goal }));
        });
        return results;
    }, [allGoals]);

    const taskSuggestions = useMemo(() => {
        if (!searchQuery.trim()) return [];
        return allTaskResults
            .filter(({ task }) => fuzzyMatch(searchQuery, task.title))
            .slice(0, 20);
    }, [searchQuery, allTaskResults]);

    // ── Handlers ───────────────────────────────────────────────────────────────

    function handleModeSwitch(mode: SearchMode) {
        setSearchMode(mode);
        setSearchQuery("");
        setSelectedFeature(null);
    }

    function handleFeatureSelect(name: string) {
        setSelectedFeature(name);
        setSearchQuery(name);
    }

    function handleSearchChange(value: string) {
        setSearchQuery(value);
        if (selectedFeature && value !== selectedFeature) setSelectedFeature(null);
    }

    function handleClear() {
        setSearchQuery("");
        setSelectedFeature(null);
    }

    const selectedIterations = selectedFeature ? (featureMap.get(selectedFeature) ?? []) : null;
    const showFeatureDropdown = searchMode === "feature" && featureSuggestions.length > 0 && !selectedFeature;
    const showTaskResults = searchMode === "task" && searchQuery.trim().length > 0;

    // ── Loading / error states ─────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full">
                <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">{heading}</h5>
                <div className="w-full flex flex-col gap-4">
                    <SkeletonLoader count={1} height="h-8" width="w-40" borderRadius="rounded-full" />
                    <SkeletonLoader count={1} height="h-10" width="w-full" borderRadius="rounded-lg" />
                    <SkeletonLoader count={1} height="h-4" width="w-2/3" />
                    <SkeletonLoader count={1} height="h-4" width="w-1/2" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full">
                <h5 className="relative font-bold text-xl-dv mt-0 mb-2 capitalize dark:text-white">{heading}</h5>
                <p className="text-sm text-red-500 m-0">Failed to load feature data. Please try again.</p>
            </div>
        );
    }

    return (
        <div className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-4 max-w-full min-w-0 w-full text-left text-base text-colors-text-black font-body-xs-semi-bold">

            {/* Header */}
            <div className="self-stretch flex flex-row items-center justify-between gap-[16px] max-w-full inner-mobile-action-card-blg-dv">
                <h5 className="relative font-bold text-xl-dv mt-0 mb-0 capitalize dark:text-white">
                    {heading}
                    <span className="ml-2 text-sm font-normal text-gray-400">
                        ({featureNames.length} features)
                    </span>
                </h5>
                {/* Archived toggle */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 rounded-lg p-1 flex-shrink-0">
                    <button
                        onClick={() => { setIsArchived(true); setSearchQuery(""); setSelectedFeature(null); }}
                        className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer border-none ${isArchived
                            ? "bg-white dark:bg-slate-800 text-gray-800 dark:text-white shadow-sm"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            }`}
                    >
                        Archived
                    </button>
                    <button
                        onClick={() => { setIsArchived(false); setSearchQuery(""); setSelectedFeature(null); }}
                        className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer border-none ${!isArchived
                            ? "bg-white dark:bg-slate-800 text-gray-800 dark:text-white shadow-sm"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            }`}
                    >
                        Active
                    </button>
                </div>
            </div>

            {/* Search mode toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 rounded-lg p-1 self-start">
                <button
                    onClick={() => handleModeSwitch("feature")}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer border-none ${searchMode === "feature"
                        ? "bg-white dark:bg-slate-800 text-gray-800 dark:text-white shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        }`}
                >
                    By Feature
                </button>
                <button
                    onClick={() => handleModeSwitch("task")}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer border-none ${searchMode === "task"
                        ? "bg-white dark:bg-slate-800 text-gray-800 dark:text-white shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        }`}
                >
                    By Task
                </button>
            </div>

            {/* Search box */}
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1  gap-[8px] min-w-full">
                <div className="relative w-full">
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                    </svg>
                    <div id="cts-dropdown-dv" className=" border-surface-border-blg-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] rounded-radius-md bg-text-text-tertiary dark:bg-slate-700 overflow-hidden h-[20px] flex flex-row items-center justify-start py-2.5 px-[15px] gap-[8px] border-[1px] border-solid border-surface-border mq450:flex-nowrap">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder={searchMode === "feature" ? "Search feature name…" : "Search task name…"}
                            className="pl-9 pr-8 py-2 text-sm featureinput-item-dv bg-white dark:bg-slate-800 text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none"
                        />
                    </div>
                    {searchQuery && (
                        <button
                            onClick={handleClear}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer border-none px-[3px] py-[0px] cta-red p-0"
                        >
                            <svg className="w-4 h-4 relative top-[2px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}

                    {/* Feature suggestions — inline, not absolute */}
                    {showFeatureDropdown && (
                        <ul className="w-full dropdown-menu-feature-dv list-decimal bg-white dark:bg-slate-800 border border-t-0 border-gray-200 dark:border-slate-600 rounded-b-lg shadow-sm p-0 m-0">
                            {featureSuggestions.map((name) => {
                                const count = featureMap.get(name)?.length ?? 0;
                                return (
                                    <li
                                        key={name}
                                        onClick={() => handleFeatureSelect(name)}
                                        className="flex items-center justify-between px-3 py-2.5 text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700 text-gray-800 dark:text-white border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                                    >
                                        <span className="flex-1">{name}</span>
                                        <span className="ml-2 flex-shrink-0 text-xs taskcount-dv bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-gray-300 px-1.5 py-0.5 rounded-full">{count}×</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>


            </div>

            {/* Results */}
            <div className="w-full min-w-0 overflow-y-auto">
                {/* <div className="w-full min-w-0 max-h-[520px] overflow-y-auto"> */}

                {/* ── Feature mode results ── */}
                {searchMode === "feature" && (
                    <>
                        {selectedIterations ? (
                            <FeatureSummary featureName={selectedFeature!} iterations={selectedIterations} />
                        ) : searchQuery && featureSuggestions.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400 m-0">
                                No features match "<strong>{searchQuery}</strong>". Try a different keyword.
                            </p>
                        ) : !searchQuery ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 m-0">
                                Start typing a feature name to explore its history.
                            </p>
                        ) : null}
                    </>
                )}

                {/* ── Task mode results ── */}
                {searchMode === "task" && (
                    <>
                        {!searchQuery ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 m-0">
                                Start typing a task name to find it.
                            </p>
                        ) : taskSuggestions.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400 m-0">
                                No tasks match "<strong>{searchQuery}</strong>". Try a different keyword.
                            </p>
                        ) : (
                            <div className="flex flex-col gap-3">
                                <p className="text-xs text-gray-400 dark:text-gray-500 m-0">
                                    {taskSuggestions.length} task{taskSuggestions.length !== 1 ? "s" : ""} found
                                </p>

                                <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1  gap-[8px] min-w-full">
                                    {taskSuggestions.map(({ task, goal }, i) => (
                                        <TaskResultCard key={`${task.id}-${i}`} task={task} goal={goal} />
                                    ))}
                                </div>


                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default FeatureTab;
